/**
 * Authenticated API client for Heapster ID backend
 * Handles automatic Firebase Auth token attachment and refresh
 */

/* eslint-disable no-console */

import { getAuth, signOut, type User } from 'firebase/auth';
import { appConfig } from './env';
import { queryClient } from './query-client';
import type { UserProfile, UserSearchResult } from './types/user';
import type { UserEntitlements } from './types/entitlement';
import type {
  AdminSubscriptionsPage,
  SubscriptionQueryParams,
} from './types/subscriptionMirror';
import type {
  RedeemGroupListResponse,
  RedeemGroupResponse,
  CreateRedeemGroupInput,
  UpdateRedeemGroupInput,
  GenerateCodesResponse,
  RedeemCodeResponse,
  RedemptionHistoryResponse,
  CodesListResponse,
  RedeemCodeApiFormat,
  ImportCodesResponse,
  BatchListResponse,
  RedeemPreviewResponse,
} from './types/redeemGroup';

export interface ApiResponse<T> {
  /**
   * `null` is a real value here, not a placeholder: a 204 response carries no
   * body and `handleResponse` returns `{ data: null }` for it. The type said
   * `T | undefined` and the 204 branch cast with `null as T` to fit, which made
   * every honest `data: null` test fixture a type error (#1744).
   */
  data?: T | null;
  error?: string;
  success: boolean;
}

/**
 * Strict RFC 3986 percent-encoding: `encodeURIComponent`, plus the five
 * characters it leaves literal (`!'()*`), which RFC 3986 reserves. The OpenAPI
 * validator refuses a reserved character left unencoded in a query value
 * unless the parameter carries `allowReserved`, so this is what makes a value
 * safe against any server, whatever its spec says.
 */
export function encodeRfc3986(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  );
}

/**
 * A non-2xx HTTP response. Thrown by `request`, carrying the `status`, so a
 * caller can act on the outcome class — the manual-entitlement runner (#2029)
 * retries a 5xx, `408` or `429` and treats any other 4xx as refused — without
 * parsing the message. The message is unchanged (`HTTP <status>: <message>`),
 * so every existing `err.message` display reads as before.
 *
 * `code` is the body's `error` category when the backend sent one — the
 * global error handler always does, e.g. `EMAIL_NOT_VERIFIED` (#2037) — so a
 * caller can tell one refusal from another of the same status without
 * matching on prose. `serverMessage` is the backend's message without the
 * `HTTP <status>: ` prefix.
 *
 * `context` is the body's `context` object when it carries one. A refusal
 * whose category is shared says which refusal it is there: the checkout 409
 * is `CONFLICT` with `context.code: HEAPSTERLIVE_SUBSCRIPTION_EXISTS` (#2218).
 */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly code?: string;
  /** The server's message, unprefixed, for callers that display it. */
  readonly serverMessage: string;
  readonly context?: Record<string, unknown>;

  constructor(
    status: number,
    message: string,
    code?: string,
    context?: Record<string, unknown>
  ) {
    super(`HTTP ${status}: ${message}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.code = code;
    this.serverMessage = message;
    this.context = context;
  }
}

/**
 * `200` body of the per-grant manual-entitlement routes (#2028).
 * `changed: false` means the id was already as asked and nothing was written.
 */
export interface ManualEntitlementChangeResponse {
  changed: boolean;
  entitlementId: string;
  /** The sorted held manual set after the call. */
  manual: string[];
  /** Every id active in storage after the call, from every source. */
  effective: string[];
}

/**
 * Result of user deletion operations
 * Tracks success/failure of each cleanup operation independently
 */
export interface DeletionResult {
  firestore: {
    success: boolean;
    error?: string;
  };
  stripe: {
    success: boolean;
    error?: string;
  };
  storage: {
    success: boolean;
    error?: string;
  };
  audit: {
    success: boolean;
    error?: string;
  };
}

/**
 * The two codes the Firebase SDK gives a token refresh the server refused:
 * `auth/user-token-expired` (refresh token revoked, or the account deleted) and
 * `auth/user-disabled`. Read structurally, because the `firebase/auth` test
 * mock exports no `FirebaseError` to test against.
 */
function isSessionEndedError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'auth/user-token-expired' || code === 'auth/user-disabled';
}

/** The forced refresh in progress, shared by every request waiting on it. */
let refreshInFlight: { user: User; promise: Promise<string | null> } | null =
  null;

let sessionEndInFlight: { user: User; promise: Promise<void> } | null = null;

/**
 * Sign out and drop the query cache: the two effects of `useSignOut`
 * (hooks/useAuth.ts), from outside React. There is no navigation here. The
 * route guards send a signed-out visitor to sign-in with `returnTo`, as they
 * do after a manual sign-out.
 *
 * The SDK has usually signed out already when it refused the refresh. The call
 * here keeps that from depending on SDK internals, and the cache clear, which
 * also cancels pending query retries, is what the SDK would not do.
 */
function endSession(user: User): Promise<void> {
  if (sessionEndInFlight?.user === user) {
    return sessionEndInFlight.promise;
  }

  const promise: Promise<void> = (async () => {
    try {
      const auth = getAuth();
      // Never sign out an account that signed in since.
      if (auth.currentUser === user || auth.currentUser === null) {
        await signOut(auth);
      }
    } catch (error) {
      console.error('Sign out after a refused token refresh failed:', error);
    }
    // The same guard for the cache: it belongs to whoever is signed in now,
    // and an account that signed in since keeps what it has loaded.
    const current = getAuth().currentUser;
    if (current === user || current === null) {
      queryClient.clear();
    }
  })().finally(() => {
    if (sessionEndInFlight?.promise === promise) {
      sessionEndInFlight = null;
    }
  });
  sessionEndInFlight = { user, promise };
  return promise;
}

/**
 * Authenticated API client that automatically includes Firebase ID tokens
 */
export class AuthenticatedApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = appConfig.api.baseUrl) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get current Firebase Auth ID token
   */
  async getAuthToken(): Promise<string | null> {
    return this.tokenFor(getAuth().currentUser);
  }

  private async tokenFor(user: User | null): Promise<string | null> {
    if (!user) {
      return null;
    }

    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Failed to get auth token:', error);
      // An unforced read refreshes a token that is close to expiry, so it can
      // be the call that learns the session is over.
      if (isSessionEndedError(error)) {
        await endSession(user);
      }
      return null;
    }
  }

  /**
   * A token to resend with after `staleToken` was answered 401, or `null` when
   * there is none to be had. One refresh is shared by every request waiting on
   * the same user (#2140).
   */
  private async freshTokenFor(
    user: User,
    staleToken: string
  ): Promise<string | null> {
    // Another request may have refreshed already, and settled before this 401
    // arrived. The unforced read returns what that refresh left.
    const current = await this.tokenFor(user);
    if (current !== staleToken) {
      return current;
    }

    if (refreshInFlight?.user !== user) {
      const promise: Promise<string | null> = user
        .getIdToken(true)
        .catch(async (error: unknown) => {
          console.error('Failed to refresh auth token:', error);
          // Only a refresh the server refused ends the session. A network
          // failure, or anything else, says nothing about whether it is over.
          if (isSessionEndedError(error)) {
            await endSession(user);
          }
          return null;
        })
        .finally(() => {
          if (refreshInFlight?.promise === promise) {
            refreshInFlight = null;
          }
        });
      refreshInFlight = { user, promise };
    }
    return refreshInFlight.promise;
  }

  /**
   * `fetch` with the current ID token, and on a 401 one resend with a
   * refreshed token (#2140). The caller owns the `AbortController`, its timer
   * and the timeout message, and passes `signal` in `init`. That signal covers
   * the two sends. It does not cover the token refresh between them, which
   * cannot be aborted and is bounded by the SDK's own network timeout; a
   * caller's timeout that fires during the refresh takes effect at the resend.
   */
  private async fetchWithAuth(
    url: string,
    init: Omit<RequestInit, 'headers'> & { headers?: Record<string, string> }
  ): Promise<Response> {
    const user = getAuth().currentUser;
    const token = await this.tokenFor(user);
    console.log('🔑 [AuthenticatedApi] Auth token', {
      hasToken: !!token,
      tokenLength: token ? token.length : 0,
    });
    if (!token) {
      console.warn('⚠️ [AuthenticatedApi] No auth token available');
    }

    const send = (bearer: string | null) =>
      fetch(url, {
        ...init,
        headers: bearer
          ? { ...init.headers, Authorization: `Bearer ${bearer}` }
          : init.headers,
      });

    const response = await send(token);
    if (response.status !== 401 || !user || !token) {
      return response;
    }

    // Signed out, or another account signed in, while the request was in
    // flight: the 401 is not about the session there is now.
    if (getAuth().currentUser !== user) {
      return response;
    }

    const freshToken = await this.freshTokenFor(user, token);
    if (!freshToken) {
      return response;
    }

    // Asked again, because the refresh took time: a request made for one user
    // is not resent once somebody else, or nobody, is signed in.
    if (getAuth().currentUser !== user) {
      return response;
    }

    // Resent whatever the method, once, and returned whatever it is. That is
    // safe only because every 401 the API gives is produced before a route
    // handler has written anything (`authMiddleware`, `strictAuthMiddleware`,
    // and the handler checks in routes/redeem.ts that precede every read), so a
    // resend cannot apply a change twice. If a handler ever answers 401 after
    // a write, this must change.
    return send(freshToken);
  }

  /**
   * Make authenticated HTTP request
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    console.log('📡 [AuthenticatedApi] Starting request', {
      endpoint,
      method: options.method || 'GET',
      baseUrl: this.baseUrl,
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    const fullUrl = `${this.baseUrl}${endpoint}`;
    console.log('📡 [AuthenticatedApi] Making fetch request', {
      url: fullUrl,
      method: options.method || 'GET',
    });

    // Add 30-second timeout to prevent infinite hangs
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await this.fetchWithAuth(fullUrl, {
        ...options,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return await this.handleResponse<T>(response, endpoint);
    } catch (error) {
      clearTimeout(timeoutId);
      // Ask the controller, don't interrogate the error (#1744).
      //
      // `error instanceof Error && error.name === 'AbortError'` looks right and
      // is not: an aborted fetch rejects with a DOMException, which inherits
      // from Error in a browser but NOT across realms — under jsdom the
      // exception comes from Node's realm while `Error` is jsdom's, so the
      // branch was skipped and callers saw the raw "The operation was aborted."
      // instead of the message below.
      //
      // Matching on `name` alone fixes that but would also match any thrown
      // value that happens to carry `name: 'AbortError'`. `signal.aborted` is
      // the actual question — did OUR timeout fire — and it is realm-
      // independent by construction, needs no cast, and cannot be spoofed.
      if (controller.signal.aborted) {
        throw new Error(`Request timeout after 30s: ${endpoint}`);
      }
      // Log error for debugging but rethrow for React Query to handle
      console.error('❌ [AuthenticatedApi] Request failed with exception', {
        error: error instanceof Error ? error.message : error,
        endpoint,
        baseUrl: this.baseUrl,
      });
      throw error;
    }
  }

  /**
   * Handle fetch response and extract data
   */
  private async handleResponse<T>(
    response: Response,
    _endpoint: string
  ): Promise<ApiResponse<T>> {
    console.log('📡 [AuthenticatedApi] Response received', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [AuthenticatedApi] Request failed', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });

      // Try to parse error as JSON to extract message field, the error
      // category when `error` is one (a string) rather than a nested object,
      // and `context` when it is an object.
      let errorMessage = errorText;
      let errorCode: string | undefined;
      let errorContext: Record<string, unknown> | undefined;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error?.message) {
          errorMessage = errorJson.error.message;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        }
        if (typeof errorJson.error === 'string') {
          errorCode = errorJson.error;
        }
        if (
          typeof errorJson.context === 'object' &&
          errorJson.context !== null &&
          !Array.isArray(errorJson.context)
        ) {
          errorContext = errorJson.context;
        }
      } catch {
        // Not JSON, use raw text
      }

      throw new ApiHttpError(
        response.status,
        errorMessage,
        errorCode,
        errorContext
      );
    }

    // Handle 204 No Content - no response body to parse
    if (response.status === 204) {
      console.log('✅ [AuthenticatedApi] Request successful (204 No Content)');
      return { data: null, success: true };
    }

    const data = await response.json();
    console.log('✅ [AuthenticatedApi] Request successful', {
      hasData: !!data,
      dataKeys: data ? Object.keys(data as Record<string, unknown>) : [],
    });
    return { data, success: true };
  }

  /**
   * Update user profile
   * Uses PUT /v1/users/{uid}/profile endpoint
   */
  async updateUserProfile(
    uid: string,
    updates: {
      display_name?: string;
      photo_url?: {
        '128'?: string;
        '512'?: string;
      };
    }
  ): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(`/v1/users/${uid}/profile`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // ===== ADMIN API METHODS =====
  // These methods require ADMIN or MODERATOR role

  /**
   * List users with search, filter, and pagination
   * Uses GET /v1/admin/users endpoint
   * Requires ADMIN role
   */
  async listUsers(params?: {
    emailSearch?: string;
    nameSearch?: string;
    limit?: number;
    cursor?: string;
  }): Promise<ApiResponse<UserSearchResult>> {
    const searchParams = new URLSearchParams();

    if (params?.emailSearch)
      searchParams.set('emailSearch', params.emailSearch);
    if (params?.nameSearch) searchParams.set('nameSearch', params.nameSearch);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.cursor) searchParams.set('cursor', params.cursor);

    const queryString = searchParams.toString();
    const endpoint = `/v1/admin/users${queryString ? `?${queryString}` : ''}`;

    return this.request<UserSearchResult>(endpoint);
  }

  /**
   * List cross-provider subscription-mirror records.
   * Uses GET /v1/admin/subscriptions. Requires `sys:admin`.
   *
   * EVERY parameter is omitted unless it carries a usable value. That is a
   * correctness requirement, not tidiness: `provider` and `normalizedStatus`
   * are `enum`s in `openapi.yaml` and `validateRequests` is on, so a
   * `?provider=` with an empty value is rejected 400 by the validator before
   * the handler — and the handler's own empty-string tolerance never gets to
   * run. A `<select>` whose "All" option is `''` must therefore not be written
   * through to the query string.
   *
   * `orphan` is sent only when true for the same reason it is read that way
   * server-side: `orphan=false` means "no user filter", not "exclude orphans".
   */
  async listSubscriptions(
    params?: SubscriptionQueryParams
  ): Promise<ApiResponse<AdminSubscriptionsPage>> {
    const searchParams = new URLSearchParams();

    if (params?.provider) searchParams.set('provider', params.provider);
    if (params?.normalizedStatus)
      searchParams.set('normalizedStatus', params.normalizedStatus);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.productId) searchParams.set('productId', params.productId);
    if (params?.uid) searchParams.set('uid', params.uid);
    if (params?.orphan === true) searchParams.set('orphan', 'true');
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.startAfter) searchParams.set('startAfter', params.startAfter);

    const queryString = searchParams.toString();
    const endpoint = `/v1/admin/subscriptions${queryString ? `?${queryString}` : ''}`;

    return this.request<AdminSubscriptionsPage>(endpoint);
  }

  /**
   * Get admin rate limit status
   * Uses GET /v1/admin/rate-limit-status endpoint
   * Requires ADMIN role
   */
  async getAdminRateLimitStatus(): Promise<
    ApiResponse<{
      requests_used: number;
      requests_limit: number;
      reset_time: string;
    }>
  > {
    return this.request(`/v1/admin/rate-limit-status`);
  }

  /**
   * Get user profile by UID (admin access)
   * Uses GET /v1/users/{uid} endpoint with admin permissions
   * Requires ADMIN or MODERATOR role
   */
  async getUserProfile(uid: string): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>(`/v1/users/${uid}`);
  }

  /**
   * Get user's active entitlements (admin access)
   * Uses GET /v1/users/{uid}/entitlements endpoint with admin permissions
   * Returns merged entitlement data with source attribution
   * Requires ADMIN or MODERATOR role
   */
  async getUserEntitlements(
    uid: string
  ): Promise<ApiResponse<UserEntitlements>> {
    return this.request<UserEntitlements>(`/v1/users/${uid}/entitlements`);
  }

  /**
   * Add ONE manual grant, leaving every other grant as it is (admin only).
   * Uses POST /v1/admin/users/{uid}/manual-entitlements/{entitlementId} (#2028).
   * A no-op (`changed: false`) when a live, non-expiring grant is already held.
   *
   * The id is percent-encoded (`encodeRfc3986`): `hlive:host` is sent as
   * `hlive%3Ahost`.
   */
  async addManualEntitlement(
    uid: string,
    entitlementId: string,
    reason: string
  ): Promise<ApiResponse<ManualEntitlementChangeResponse>> {
    return this.request<ManualEntitlementChangeResponse>(
      `/v1/admin/users/${encodeURIComponent(uid)}/manual-entitlements/${encodeRfc3986(entitlementId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }
    );
  }

  /**
   * Remove ONE manual grant, leaving every other grant as it is (admin only).
   * Uses DELETE /v1/admin/users/{uid}/manual-entitlements/{entitlementId}?reason=
   * (#2028). A no-op (`changed: false`) when no manual record is stored.
   *
   * The reason travels in the query string, so it is percent-encoded, and
   * strictly (`encodeRfc3986`): an `&` or `#` in free text would otherwise cut
   * it short, and every reserved character, `()!'*` included, is encoded. So
   * the request needs no `allowReserved` on the server and is accepted by any
   * deployed version of the spec, even one older than this client. Not
   * `URLSearchParams`, which writes a space as `+` (form encoding) and leaves
   * other reserved characters literal. The server's `reason` parameter carries
   * `allowReserved` since #2029, so it accepts such looser clients too.
   */
  async removeManualEntitlement(
    uid: string,
    entitlementId: string,
    reason: string
  ): Promise<ApiResponse<ManualEntitlementChangeResponse>> {
    return this.request<ManualEntitlementChangeResponse>(
      `/v1/admin/users/${encodeURIComponent(uid)}/manual-entitlements/${encodeRfc3986(entitlementId)}?reason=${encodeRfc3986(reason)}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Check if a badge exists by ID (admin only)
   * Uses GET /v1/admin/badges/{badgeId} endpoint
   * Requires ADMIN role
   */
  async checkBadgeExists(badgeId: string): Promise<ApiResponse<unknown>> {
    return this.request<unknown>(`/v1/admin/badges/${badgeId}`);
  }

  /**
   * Delete user account (admin only)
   * Uses DELETE /v1/admin/users/{uid} endpoint
   * Requires ADMIN role
   * Permanently deletes user data across Firestore, Stripe, and Cloud Storage
   */
  async deleteUser(
    uid: string,
    reason: string
  ): Promise<ApiResponse<DeletionResult>> {
    return this.request<DeletionResult>(`/v1/admin/users/${uid}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  }

  // ===== REDEEM GROUP API METHODS =====
  // These methods require ADMIN role

  /**
   * List redeem groups with pagination
   * Uses GET /v1/admin/redeem-groups endpoint
   * Requires ADMIN role
   */
  async listRedeemGroups(params?: {
    limit?: number;
    startAfter?: string;
  }): Promise<ApiResponse<RedeemGroupListResponse>> {
    const searchParams = new URLSearchParams();

    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.startAfter) searchParams.set('startAfter', params.startAfter);

    const queryString = searchParams.toString();
    const endpoint = `/v1/admin/redeem-groups${queryString ? `?${queryString}` : ''}`;

    return this.request<RedeemGroupListResponse>(endpoint);
  }

  /**
   * Get a single redeem group by ID
   * Uses GET /v1/admin/redeem-groups/:groupId endpoint
   * Requires ADMIN role
   */
  async getRedeemGroup(
    groupId: string
  ): Promise<ApiResponse<RedeemGroupResponse>> {
    return this.request<RedeemGroupResponse>(
      `/v1/admin/redeem-groups/${groupId}`
    );
  }

  /**
   * Create a new redeem group
   * Uses POST /v1/admin/redeem-groups endpoint
   * Requires ADMIN role
   */
  async createRedeemGroup(
    data: CreateRedeemGroupInput
  ): Promise<ApiResponse<RedeemGroupResponse>> {
    return this.request<RedeemGroupResponse>('/v1/admin/redeem-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a redeem group
   * Uses PUT /v1/admin/redeem-groups/:groupId endpoint
   * Requires ADMIN role
   */
  async updateRedeemGroup(
    groupId: string,
    updates: UpdateRedeemGroupInput
  ): Promise<ApiResponse<RedeemGroupResponse>> {
    return this.request<RedeemGroupResponse>(
      `/v1/admin/redeem-groups/${groupId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }

  /**
   * Delete a redeem group
   * Uses DELETE /v1/admin/redeem-groups/:groupId endpoint
   * Requires ADMIN role
   */
  async deleteRedeemGroup(groupId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/v1/admin/redeem-groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Generate redeem codes for a group
   * Uses POST /v1/admin/redeem-groups/:groupId/generate endpoint
   * Requires ADMIN role
   */
  async generateCodes(
    groupId: string,
    count: number,
    note?: string
  ): Promise<ApiResponse<GenerateCodesResponse>> {
    return this.request<GenerateCodesResponse>(
      `/v1/admin/redeem-groups/${groupId}/generate`,
      {
        method: 'POST',
        body: JSON.stringify({ count, ...(note && { note }) }),
      }
    );
  }

  /**
   * Get codes for a redeem group with pagination and filtering
   * Uses GET /v1/admin/redeem-groups/:groupId/codes endpoint
   * Requires ADMIN role
   */
  async getGroupCodes(
    groupId: string,
    params?: {
      limit?: number;
      startAfter?: string;
      redeemed?: boolean;
      batchId?: string;
    }
  ): Promise<ApiResponse<CodesListResponse>> {
    const searchParams = new URLSearchParams();

    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.startAfter) searchParams.set('startAfter', params.startAfter);
    if (params?.redeemed !== undefined)
      searchParams.set('redeemed', params.redeemed.toString());
    if (params?.batchId) searchParams.set('batchId', params.batchId);

    const queryString = searchParams.toString();
    const endpoint = `/v1/admin/redeem-groups/${groupId}/codes${queryString ? `?${queryString}` : ''}`;

    return this.request<CodesListResponse>(endpoint);
  }

  /**
   * List batches for a redeem group with pagination
   * Uses GET /v1/admin/redeem-groups/:groupId/batches endpoint
   * Requires ADMIN role
   */
  async listBatches(
    groupId: string,
    params?: {
      limit?: number;
      startAfter?: string;
    }
  ): Promise<ApiResponse<BatchListResponse>> {
    const searchParams = new URLSearchParams();

    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.startAfter) searchParams.set('startAfter', params.startAfter);

    const queryString = searchParams.toString();
    const endpoint = `/v1/admin/redeem-groups/${groupId}/batches${queryString ? `?${queryString}` : ''}`;

    return this.request<BatchListResponse>(endpoint);
  }

  /**
   * Download redeem codes as CSV for a specific batch
   * Uses GET /v1/admin/redeem-groups/:groupId/codes/csv endpoint
   * Requires ADMIN role
   * Returns a blob containing the CSV data
   *
   * @param groupId - The group to export codes from
   * @param options - Optional filters: batchId (specific batch) and redeemed (filter by status)
   */
  async downloadGroupCodesCSV(
    groupId: string,
    options?: { batchId?: string; redeemed?: boolean }
  ): Promise<Blob> {
    // Build query string with optional parameters
    const params = new URLSearchParams();
    if (options?.batchId) {
      params.set('batchId', options.batchId);
    }
    if (options?.redeemed !== undefined) {
      params.set('redeemed', String(options.redeemed));
    }

    const queryString = params.toString();
    const endpoint = `/v1/admin/redeem-groups/${groupId}/codes/csv${queryString ? `?${queryString}` : ''}`;
    const fullUrl = `${this.baseUrl}${endpoint}`;

    // Add 60-second timeout for large CSV downloads
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await this.fetchWithAuth(fullUrl, {
        signal: controller.signal,
      });

      if (!response.ok) {
        clearTimeout(timeoutId);
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Download the blob body - timeout continues to protect this phase
      const blob = await response.blob();
      clearTimeout(timeoutId);

      // Verify we got some data (Content-Length check removed as streaming
      // responses don't provide it, and blob.size > 0 is sufficient validation)
      if (blob.size === 0) {
        throw new Error('Empty CSV file received');
      }

      return blob;
    } catch (error) {
      clearTimeout(timeoutId);
      // See the note on `request` above: ask the controller, not the error.
      if (controller.signal.aborted) {
        throw new Error(`Request timeout after 60s: ${endpoint}`);
      }
      throw error;
    }
  }

  /**
   * Get status of a specific redeem code
   * Uses GET /v1/admin/redeem-groups/codes/:code endpoint
   * Returns code metadata including redemption status
   * Requires ADMIN role
   */
  async getCodeStatus(code: string): Promise<ApiResponse<RedeemCodeApiFormat>> {
    return this.request<RedeemCodeApiFormat>(
      `/v1/admin/redeem-groups/codes/${code}`
    );
  }

  /**
   * Import redeem codes from CSV file with automatic chunking
   * Uses POST /v1/admin/redeem-groups/:groupId/import endpoint
   * Requires ADMIN role
   *
   * Automatically splits large imports into 400-code chunks to respect
   * backend Firestore transaction limits. Provides progress callbacks.
   *
   * The 10MB limit is CodeImportModal's, on the file it is given. The API's
   * limits are per request: 400 codes and a 1MB body. Each request sent here
   * is a multipart/form-data CSV of at most 400 codes, about 9KB.
   */
  async importCodes(
    groupId: string,
    file: File,
    note?: string,
    onProgress?: (current: number, total: number) => void
  ): Promise<ApiResponse<ImportCodesResponse>> {
    // Dynamic import to avoid bundling issues
    const { parseCSV, chunkArray } = await import('./utils/csvParser');

    // Parse CSV on frontend to enable chunking
    const codes = await parseCSV(file);

    if (codes.length === 0) {
      throw new Error('CSV file contains no valid codes');
    }

    // Backend limit is 400 codes per batch
    const BATCH_SIZE = 400;

    // If within single batch limit, use direct upload
    if (codes.length <= BATCH_SIZE) {
      return this.importCodesBatch(groupId, codes, note);
    }

    // Split into chunks for large imports
    const chunks = chunkArray(codes, BATCH_SIZE);
    console.log(
      `📦 [ImportCodes] Splitting ${codes.length} codes into ${chunks.length} batches`
    );

    // Aggregate results across all chunks
    let totalImported = 0;
    let totalVoided = 0;
    const allErrors: Array<{ row: number; code: string; error: string }> = [];

    // Process chunks sequentially to avoid overwhelming backend
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      console.log(
        `📤 [ImportCodes] Processing batch ${i + 1}/${chunks.length} (${chunk.length} codes)`
      );

      // Report progress
      if (onProgress) {
        const processedCodes = i * BATCH_SIZE;
        onProgress(processedCodes, codes.length);
      }

      try {
        // Import this chunk (note only added to first batch)
        const response = await this.importCodesBatch(
          groupId,
          chunk,
          i === 0 ? note : undefined
        );

        if (response.data) {
          totalImported += response.data.imported;
          totalVoided += response.data.voided;

          // Adjust row numbers in errors to reflect position in original CSV
          const adjustedErrors = response.data.errors.map(err => ({
            ...err,
            row: err.row + i * BATCH_SIZE,
          }));
          allErrors.push(...adjustedErrors);
        }

        console.log(
          `✅ [ImportCodes] Batch ${i + 1}/${chunks.length} completed: ${response.data?.imported} imported, ${response.data?.voided} voided, ${response.data?.errors.length} errors`
        );
      } catch (error) {
        // If a chunk fails, record errors for all codes in that chunk
        console.error(
          `❌ [ImportCodes] Batch ${i + 1}/${chunks.length} failed:`,
          error
        );

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        // Add errors for each code in failed chunk
        for (let j = 0; j < chunk.length; j++) {
          allErrors.push({
            row: i * BATCH_SIZE + j + 1,
            code: chunk[j].code,
            error: `Batch import failed: ${errorMessage}`,
          });
        }

        // A 401 that outlived the refresh-and-resend is likely to repeat, and
        // after a sign-out the later batches would go without a token at all,
        // so they are recorded as failed rather than sent.
        if (error instanceof ApiHttpError && error.status === 401) {
          for (let k = i + 1; k < chunks.length; k++) {
            for (let j = 0; j < chunks[k].length; j++) {
              allErrors.push({
                row: k * BATCH_SIZE + j + 1,
                code: chunks[k][j].code,
                error: `Batch import failed: ${errorMessage}`,
              });
            }
          }
          break;
        }
      }
    }

    // Report final progress
    if (onProgress) {
      onProgress(codes.length, codes.length);
    }

    console.log(
      `🎉 [ImportCodes] Completed: ${totalImported} imported, ${totalVoided} voided, ${allErrors.length} errors`
    );

    return {
      success: true,
      data: {
        imported: totalImported,
        voided: totalVoided,
        errors: allErrors,
      },
    };
  }

  /**
   * Import a single batch of codes (internal helper)
   * Converts parsed codes back to CSV format for backend
   */
  private async importCodesBatch(
    groupId: string,
    codes: Array<{ code: string; action?: 'VOID' }>,
    note?: string
  ): Promise<ApiResponse<ImportCodesResponse>> {
    // Convert codes back to CSV format
    const csvLines = ['CODE,ACTION'];
    for (const { code, action } of codes) {
      csvLines.push(`${code},${action || ''}`);
    }
    const csvContent = csvLines.join('\n');

    // Create a Blob as a File to send to backend
    const csvBlob = new Blob([csvContent], { type: 'text/csv' });
    const csvFile = new File([csvBlob], 'codes.csv', { type: 'text/csv' });

    const formData = new FormData();
    formData.append('file', csvFile);
    if (note) {
      formData.append('note', note);
    }

    // Add 30-second timeout per batch
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/v1/admin/redeem-groups/${groupId}/import`,
        {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        }
      );
      clearTimeout(timeoutId);
      return await this.handleResponse<ImportCodesResponse>(
        response,
        `/v1/admin/redeem-groups/${groupId}/import`
      );
    } catch (error) {
      clearTimeout(timeoutId);
      // See the note on `request` above: ask the controller, not the error.
      if (controller.signal.aborted) {
        throw new Error(
          `Request timeout after 30s: /v1/admin/redeem-groups/${groupId}/import`
        );
      }
      throw error;
    }
  }

  /**
   * Upload badge icon
   * Uses POST /v1/admin/badges/:badgeId/icon endpoint
   * Accepts multipart/form-data with SVG file (max 256KB)
   * Requires ADMIN role
   */
  async uploadBadgeIcon(
    badgeId: string,
    file: File
    //
    // Returns the updated badge, NOT top-level icon fields — this type claimed
    // `{ icon_url, icon_updated_at }` until #1883, matching a spec block that was
    // equally wrong. `unknown` rather than a shape, matching this file's other
    // badge methods: the only caller truthiness-checks the response and discards
    // it, and inventing a type nothing checks is how the last one survived.
  ): Promise<ApiResponse<{ badge: unknown }>> {
    const formData = new FormData();
    formData.append('icon', file);

    // Add 30-second timeout to prevent infinite hangs
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await this.fetchWithAuth(
        `${this.baseUrl}/v1/admin/badges/${badgeId}/icon`,
        {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        }
      );
      clearTimeout(timeoutId);
      return await this.handleResponse<{ badge: unknown }>(
        response,
        `/v1/admin/badges/${badgeId}/icon`
      );
    } catch (error) {
      clearTimeout(timeoutId);
      // See the note on `request` above: ask the controller, not the error.
      if (controller.signal.aborted) {
        throw new Error(
          `Request timeout after 30s: /v1/admin/badges/${badgeId}/icon`
        );
      }
      throw error;
    }
  }

  // ===== USER REDEMPTION API METHODS =====
  // These methods require authenticated user

  /**
   * Redeem a promotional code for the authenticated user
   * Uses POST /v1/redeem/:code endpoint
   * Grants entitlements and badges from the code's group
   * Requires authentication
   */
  async redeemCode(code: string): Promise<ApiResponse<RedeemCodeResponse>> {
    return this.request<RedeemCodeResponse>(
      `/v1/redeem/${encodeURIComponent(code)}`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Get authenticated user's redemption history with pagination
   * Uses GET /v1/redeem/history endpoint
   * Requires authentication
   */
  async getRedemptionHistory(
    limit?: number,
    startAfter?: string
  ): Promise<ApiResponse<RedemptionHistoryResponse>> {
    const searchParams = new URLSearchParams();

    if (limit) searchParams.set('limit', limit.toString());
    if (startAfter) searchParams.set('startAfter', startAfter);

    const queryString = searchParams.toString();
    const endpoint = `/v1/redeem/history${queryString ? `?${queryString}` : ''}`;

    return this.request<RedemptionHistoryResponse>(endpoint);
  }

  /**
   * Preview what a promotional code would grant without redeeming it
   * Uses GET /v1/redeem/:code/preview endpoint
   * Returns entitlementIds, badgeIds, and groupName
   * Requires authentication
   */
  async getRedeemPreview(
    code: string
  ): Promise<ApiResponse<RedeemPreviewResponse>> {
    return this.request<RedeemPreviewResponse>(
      `/v1/redeem/${encodeURIComponent(code)}/preview`
    );
  }

  // ===== Agreements API =====

  async listAgreements(params?: {
    includeDisabled?: boolean;
  }): Promise<ApiResponse<import('./types/agreement').ListAgreementsResponse>> {
    const qs = new URLSearchParams();
    if (params?.includeDisabled !== undefined) {
      qs.set('includeDisabled', String(params.includeDisabled));
    }
    const query = qs.toString();
    return this.request(`/v1/admin/agreements${query ? `?${query}` : ''}`);
  }

  async getAgreementAdmin(
    slug: string
  ): Promise<ApiResponse<import('./types/agreement').AgreementWithBody>> {
    return this.request(`/v1/admin/agreements/${encodeURIComponent(slug)}`);
  }

  async getAgreementPublic(
    slug: string
  ): Promise<ApiResponse<import('./types/agreement').AgreementWithBody>> {
    return this.request(`/v1/agreements/${encodeURIComponent(slug)}`);
  }

  async createAgreement(
    input: import('./types/agreement').CreateAgreementInput
  ): Promise<ApiResponse<import('./types/agreement').AgreementWithBody>> {
    return this.request(`/v1/admin/agreements`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateAgreementBody(
    slug: string,
    input: import('./types/agreement').UpdateAgreementBodyInput
  ): Promise<ApiResponse<import('./types/agreement').AgreementWithBody>> {
    return this.request(
      `/v1/admin/agreements/${encodeURIComponent(slug)}/body`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
  }

  async updateAgreementMeta(
    slug: string,
    input: import('./types/agreement').UpdateAgreementMetaInput
  ): Promise<ApiResponse<import('./types/agreement').Agreement>> {
    return this.request(`/v1/admin/agreements/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  async listAgreementVersions(
    slug: string
  ): Promise<ApiResponse<import('./types/agreement').ListVersionsResponse>> {
    return this.request(
      `/v1/admin/agreements/${encodeURIComponent(slug)}/versions`
    );
  }

  async getAgreementVersion(
    slug: string,
    version: number
  ): Promise<ApiResponse<import('./types/agreement').AgreementVersion>> {
    return this.request(
      `/v1/admin/agreements/${encodeURIComponent(slug)}/versions/${version}`
    );
  }

  async listUserAgreements(
    uid: string
  ): Promise<
    ApiResponse<import('./types/agreement').ListUserResponsesResponse>
  > {
    return this.request(`/v1/users/${encodeURIComponent(uid)}/agreements`);
  }

  async getUserAgreement(
    uid: string,
    slug: string
  ): Promise<ApiResponse<import('./types/agreement').UserAgreementResponse>> {
    return this.request(
      `/v1/users/${encodeURIComponent(uid)}/agreements/${encodeURIComponent(slug)}`
    );
  }

  async submitUserAgreementResponse(
    uid: string,
    slug: string,
    input: import('./types/agreement').SubmitAgreementResponseInput
  ): Promise<ApiResponse<import('./types/agreement').UserAgreementResponse>> {
    return this.request(
      `/v1/users/${encodeURIComponent(uid)}/agreements/${encodeURIComponent(slug)}`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  }

  async getUserAgreementHistory(
    uid: string,
    slug: string,
    params?: { limit?: number; startAfter?: string | null }
  ): Promise<
    ApiResponse<import('./types/agreement').UserAgreementHistoryListResponse>
  > {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.startAfter) qs.set('startAfter', params.startAfter);
    const query = qs.toString();
    return this.request(
      `/v1/users/${encodeURIComponent(uid)}/agreements/${encodeURIComponent(slug)}/history${
        query ? `?${query}` : ''
      }`
    );
  }
}

// Export singleton instance
export const authenticatedApi = new AuthenticatedApiClient();
